// biome-ignore lint/correctness/noUnusedImports: required for apps using jsx: "react" (legacy JSX transform)
import React, { memo, type RefObject, useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { ErrorBoundary } from '../components/errorBoundary';
import { usePdfCanvasManager } from '../hooks/usePdfCanvasManager';
import { usePdfPageScale } from '../hooks/usePdfPageScale';
import { type TileLayout, usePdfRenderer } from '../hooks/usePdfRenderer';
import { type IPdfImages, RENDER_TYPE } from '../types';
import { perfEnd } from '../utils/perfMetrics';
import { PdfTextLayer, type PdfTextLayerHandle } from './PdfTextLayer';

/**
 * PDF 页面组件 Props 接口
 */
interface PdfPageProps {
    pageNum: number; // 页码
    // biome-ignore lint/suspicious/noExplicitAny: PDF.js pdfDoc
    pdfDoc: any; // PDF 文档对象
    scale: number; // 视觉缩放比例（驱动布局）
    /** canvas 渲染 scale（Canvas 模式下仅超阈值时更新；其他模式与 scale 相同） */
    renderScale?: number;
    /** 全屏状态，变化时触发重新渲染（兼容 Safari 全屏切换后内容不展示） */
    screenState?: 'no-full' | 'full';
    visible?: boolean; // 是否可见（用于虚拟化）
    registerTextLayer?: (pageNum: number, textLayerRef: RefObject<HTMLDivElement>) => void; // 注册文本层回调
    unregisterTextLayer?: (pageNum: number) => void; // 注销文本层回调
    renderType?: RENDER_TYPE; // 渲染类型
    renderPixelRadio?: number; // 像素比
    pdfImages?: IPdfImages; // PDF 图片列表（用于 Image 渲染模式）
    onMeasure?: (index: number, height: number) => void; // 高度测量回调 (V2 Layout)
    pageIndex?: number; // 0-based 索引 (V2 Layout)
    onInternalLinkClick?: (pageNum: number) => void; // 内部书签跳转回调
    enableCjkFix?: boolean; // iOS CJK PUA 字体修复开关
}

/** 在 ErrorBoundary 内部抛出错误，使其捕获并展示降级 UI。 */
function ThrowError({ error }: { error: Error }): never {
    throw error;
}

export const PdfPage = memo((props: PdfPageProps) => {
    const {
        pageNum,
        pdfDoc,
        scale,
        renderScale,
        screenState,
        visible = true,
        registerTextLayer,
        unregisterTextLayer,
        renderType = RENDER_TYPE.CANVAS,
        renderPixelRadio,
        pdfImages,
        onMeasure,
        pageIndex,
        onInternalLinkClick,
        enableCjkFix,
    } = props;

    // ── Scale & CSS transform ──────────────────────────────────────────────────
    const { effectiveRenderScale, cssTransformRatio, needsTransform, notifyRenderComplete } = usePdfPageScale({
        scale,
        renderScale,
        renderType,
        pageNum,
    });

    // ── DOM refs ───────────────────────────────────────────────────────────────
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const textLayerHandle = useRef<PdfTextLayerHandle>(null);
    const annotationLayerRef = useRef<HTMLDivElement>(null);
    const svgContainerRef = useRef<HTMLDivElement>(null);
    const pageContainerRef = useRef<HTMLDivElement>(null);

    // ── Canvas GPU 内存管理 ────────────────────────────────────────────────────
    usePdfCanvasManager(canvasRef, visible);

    // ── 高度测量 (V2 Layout) ───────────────────────────────────────────────────
    useEffect(() => {
        if (!(onMeasure && pdfDoc) || pageIndex === undefined) return;
        let isCancelled = false;
        // biome-ignore lint/suspicious/noExplicitAny: PDF.js page object
        void pdfDoc.getPage(pageNum).then((page: any) => {
            if (isCancelled) return;
            const viewport = page.getViewport({ scale: 1 });
            onMeasure(pageIndex, viewport.height);
        });
        return () => {
            isCancelled = true;
        };
    }, [pdfDoc, pageNum, onMeasure, pageIndex]);

    // ── PDF.js 页面资源释放 ────────────────────────────────────────────────────
    // 组件卸载时调用 page.cleanup() 释放 display list / 字体缓存，降低内存峰值。
    // 属于渲染资源管理，由 PdfPage 负责（而非文本层）。
    // cleanup() 不影响后续重渲染，PDF.js 下次 render 时会重新解析。
    useEffect(() => {
        return () => {
            if (pdfDoc) {
                pdfDoc
                    .getPage(pageNum)
                    // biome-ignore lint/suspicious/noExplicitAny: PDF.js page object
                    .then((page: any) => page.cleanup())
                    .catch((_e: unknown) => {
                        // cleanup 失败不影响功能，静默忽略
                    });
            }
        };
    }, [pageNum, pdfDoc]);

    const [renderKey, setRenderKey] = useState(0);

    // tileLayout 在 usePdfRenderer 之后才可用，用 ref 让 onRenderSuccess 读到最新值
    // 而不需要将 tileLayout 加入 useCallback 依赖（避免每次 layout 变化重新注册回调）
    const tileLayoutRef = useRef<TileLayout | null>(null);

    // ── 渲染成功回调 ───────────────────────────────────────────────────────────
    const onRenderSuccess = useCallback(() => {
        perfEnd('page_switch', String(pageNum), { renderType, scale: effectiveRenderScale });
        perfEnd('scale', String(pageNum), { renderType, scale: effectiveRenderScale });
        // 内存超限降倍渲染时，用实际渲染 scale 通知，确保 CSS transform 以正确比例补偿
        notifyRenderComplete(tileLayoutRef.current?.renderScale ?? effectiveRenderScale);
        textLayerHandle.current?.registerIfMounted(pageNum);
        // 触发布局重测：canvas 尺寸已更新，需重新计算 visualHeight/visualWidth
        setRenderKey((k) => k + 1);
    }, [pageNum, renderType, effectiveRenderScale, notifyRenderComplete]);

    // ── 渲染调度 ───────────────────────────────────────────────────────────────
    const {
        tileLayout,
        registerTileCanvas,
        error: renderError,
    } = usePdfRenderer({
        pdfDoc,
        pageNum,
        scale: effectiveRenderScale,
        screenState,
        renderType,
        renderPixelRadio,
        canvasRef,
        svgContainerRef,
        annotationLayerRef,
        pdfImages,
        visible,
        onRenderSuccess,
        onInternalLinkClick,
        enableCjkFix,
    });

    // 同步到 ref，onRenderSuccess 读取实际渲染 scale
    tileLayoutRef.current = tileLayout;

    const [visualHeight, setVisualHeight] = useState<number | undefined>();
    const [visualWidth, setVisualWidth] = useState<number | undefined>();
    useLayoutEffect(() => {
        const el = pageContainerRef.current;

        // 分块模式
        if (tileLayout) {
            // 外层容器精确限定到视觉尺寸（pageCss × cssTransformRatio）。
            // 放大时（cssTransformRatio > 1）必须同时在 .pdf-page-container 上固定 width = pageCssW，
            // 否则块级元素会填满外层容器（pageCssW * ratio），再经 scale(ratio) 变成 ratio²，
            // 导致实际视觉宽度超出外层 overflow:hidden 边界，右侧被裁切。
            setVisualHeight(tileLayout.pageCssH * cssTransformRatio);
            setVisualWidth(tileLayout.pageCssW * cssTransformRatio);
            return;
        }

        if (!(el && needsTransform)) {
            setVisualHeight(undefined);
            setVisualWidth(undefined);
            return;
        }
        if (cssTransformRatio > 1) {
            //       导致每次 layout 后 visualWidth = visualWidth × ratio 的反馈放大循环。
            setVisualHeight(el.offsetHeight * cssTransformRatio);
            if (screenState === 'full') {
                const canvasW = canvasRef.current?.offsetWidth || 0;
                setVisualWidth(canvasW > 0 ? canvasW * cssTransformRatio : undefined);
            } else {
                setVisualWidth(undefined);
            }
        } else if (screenState === 'full') {
            //       补偿后防止垂直滚动模式下页面内容相互重叠（"文档部分展示"）。
            setVisualHeight(el.offsetHeight * cssTransformRatio);
            setVisualWidth(el.scrollWidth * cssTransformRatio);
        } else {
            // 非全屏缩小：使用 transformOrigin:top center，内容自动居中，不需要补偿。
            setVisualHeight(undefined);
            setVisualWidth(undefined);
        }
    }, [needsTransform, cssTransformRatio, screenState, tileLayout, renderKey]);
    return (
        <ErrorBoundary>
            {/* 渲染错误（canvas context 不可用、pdf.js 解码失败等）在 usePdfRenderer 中被捕获后
                仅写入本地 state，不向上传递——用户看到空白画布，以为"组件未渲染"。
                此处在 ErrorBoundary 内部重新抛出，触发 ErrorBoundary 的降级 UI，让用户得到明确反馈。 */}
            {renderError && renderError.name !== 'RenderingCancelledException' && <ThrowError error={renderError} />}
            <div
                style={
                    visualHeight !== undefined
                        ? visualWidth !== undefined
                            ? cssTransformRatio <= 1
                                ? { height: visualHeight, width: visualWidth, overflow: 'hidden', margin: '0 auto' }
                                : { height: visualHeight, width: visualWidth, overflow: 'hidden' }
                            : { height: visualHeight }
                        : undefined
                }
            >
                <div
                    ref={pageContainerRef}
                    className="pdf-page-container"
                    style={
                        needsTransform
                            ? tileLayout
                                ? {
                                    // 分块放大（iOS 内存降级等场景）：
                                    // 固定内层宽度为 pageCssW，从左上角 scale，视觉宽度 = pageCssW × ratio。
                                    // 外层 overflow:hidden 已设为 pageCssW × ratio，内容恰好填满，不溢出也不缺失。
                                    // 若不固定宽度，块级元素会撑满外层（pageCssW * ratio），
                                    // 再经 scale(ratio) 变成 ratio²，右侧被 overflow:hidden 裁切。
                                    // margin: 0 覆盖 CSS 的 margin: 0 auto——transformOrigin 为 top left 时，
                                    // auto margin 会将元素右移，导致 scale 后左侧空白、右侧内容被裁切。
                                    transform: `scale(${cssTransformRatio})`,
                                    transformOrigin: 'top left',
                                    width: tileLayout.pageCssW,
                                    margin: 0,
                                }
                                : {
                                    transform: `scale(${cssTransformRatio})`,
                                    // 缩小（ratio < 1）：从 top left 缩放，视觉左边缘对齐 x=0；
                                    // 放大（ratio >= 1）：容器由 CSS width:fit-content 收缩到 canvas 宽，
                                    //   再由 CSS margin:0 auto 居中，从 top center 对称放大。
                                    // 不覆盖 width/margin，保持 fit-content + margin:0 auto，
                                    // 确保 canvas.offsetLeft 始终为 0，annotation/text 层无需偏移修正。
                                    transformOrigin: cssTransformRatio >= 1 ? 'top center' : 'top left',
                                }
                            : undefined
                    }
                >
                    {!tileLayout && <canvas ref={canvasRef} style={{ margin: '0 auto' }} />}
                    {tileLayout && (
                        <div
                            style={{
                                position: 'relative',
                                width: tileLayout.pageCssW,
                                height: tileLayout.pageCssH,
                                margin: '0 auto',
                                display: 'block',
                            }}
                        >
                            {tileLayout.tiles.map((tile) => (
                                <canvas
                                    key={`${tile.col}-${tile.row}`}
                                    ref={(el) => registerTileCanvas(tile.col, tile.row, el)}
                                    style={{
                                        position: 'absolute',
                                        left: tile.cssX,
                                        top: tile.cssY,
                                        display: 'block',
                                    }}
                                />
                            ))}
                        </div>
                    )}

                    {/* SVG 容器：SVG 渲染模式 */}
                    <div hidden={renderType !== RENDER_TYPE.SVG} className="pdf-svg-container" ref={svgContainerRef} />

                    {/* 注释层：链接、标注等交互元素（由 AnnotationsLayer 服务在渲染完成后填充） */}
                    <div className="pdf-annotation-layer" ref={annotationLayerRef} />

                    {/* 文本层：搜索高亮覆盖层（位置由 mountTextLayerToDom 动态设置） */}
                    <PdfTextLayer
                        ref={textLayerHandle}
                        pageNum={pageNum}
                        registerTextLayer={registerTextLayer}
                        unregisterTextLayer={unregisterTextLayer}
                    />
                </div>
            </div>
        </ErrorBoundary>
    );
});
